/**
 * Protected Route Component
 * Restricts access to routes based on authentication and role
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { UserRole } from './msalConfig';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  fallbackPath?: string;
}

/**
 * Loading spinner component
 */
const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-100">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Authenticating...</p>
    </div>
  </div>
);

/**
 * Access denied component for insufficient permissions
 */
const AccessDenied: React.FC<{ requiredRole: UserRole }> = ({ requiredRole }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-100">
    <div className="text-center max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="text-red-500 text-5xl mb-4">
        <svg
          className="mx-auto h-16 w-16"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
      <p className="text-gray-600 mb-4">
        You don't have permission to access this page.
        {requiredRole !== 'Unassigned' && (
          <span className="block mt-2">
            Required role: <strong>{requiredRole}</strong>
          </span>
        )}
      </p>
      <a
        href="/"
        className="inline-block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
      >
        Return to Dashboard
      </a>
    </div>
  </div>
);

/**
 * Unassigned user component
 */
export const UnassignedUser: React.FC = () => {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="text-center max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-yellow-500 text-5xl mb-4">
          <svg
            className="mx-auto h-16 w-16"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Pending</h2>
        <p className="text-gray-600 mb-4">
          Welcome, <strong>{user?.displayName || 'User'}</strong>!
        </p>
        <p className="text-gray-600 mb-6">
          Your account has been authenticated, but no dashboard role is assigned to
          it. Access is granted through Entra ID group membership. Please contact
          your administrator to request access.
        </p>
        <button
          onClick={() => logout()}
          className="inline-block bg-slate-600 text-white px-6 py-2 rounded-md hover:bg-slate-700 transition-colors"
        >
          Sign Out
        </button>
      </div>
    </div>
  );
};

/** TSM whose store service groups did not resolve to any location. */
export const TsmLocationUnresolved: React.FC = () => {
  const { logout } = useAuth();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="text-center max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-yellow-500 text-5xl mb-4">
          <svg
            className="mx-auto h-16 w-16"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Location Not Resolved</h2>
        <p className="text-gray-600 mb-6">
          Your account is a Team Service Manager, but it is not a member of a store Service group, so no location could be resolved. Please ask IT to add you to your store's Service group in Entra ID.
        </p>
        <button
          onClick={() => logout()}
          className="inline-block bg-slate-600 text-white px-6 py-2 rounded-md hover:bg-slate-700 transition-colors"
        >
          Sign Out
        </button>
      </div>
    </div>
  );
};

/**
 * Protected Route wrapper
 * Ensures user is authenticated and has required role before rendering children
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  fallbackPath = '/login',
}) => {
  const { isAuthenticated, isLoading, user, assignedLocationIds, hasRole } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Show unassigned user message if user has no role
  if (user?.role === 'Unassigned') {
    return <UnassignedUser />;
  }

  if (user?.role === 'TeamServiceManager' && assignedLocationIds.length === 0) {
    return <TsmLocationUnresolved />;
  }

  // Check role requirement if specified
  if (requiredRole && !hasRole(requiredRole)) {
    return <AccessDenied requiredRole={requiredRole} />;
  }

  return <>{children}</>;
};

/**
 * RequireRole component for conditional rendering within pages
 */
interface RequireRoleProps {
  role: UserRole;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const RequireRole: React.FC<RequireRoleProps> = ({
  role,
  children,
  fallback = null,
}) => {
  const { hasRole } = useAuth();

  if (!hasRole(role)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};
